import { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { showLoading } from '../../containers/common/store/slices/application'
import { executeGetReservationAcceptability, executePutReservationAccept, resultErrorCodePutReservationAccept } from '../../dataAccess/webApi/dao/reservationAcceptabilityDao'
import { fromApiYmd, fromApiYmdHms } from '../../utils/dateUtil'
import { nullPropsToUndefined } from '../../utils/objectUtil'
import { useErrorHandle } from '../common/error/errorHandler'
import { useHistory, useParams } from 'react-router-dom'
import { OperationId } from '../common/constant/operationLog'
import { useOperationLog } from '../common/operationLog'
import { castNonNullable } from '../../utils/typeUtil'
import { undefinedPropsToNull } from '../../utils/objectUtil'
import { differenceInMilliseconds } from 'date-fns'
import { translate } from '../../i18n'

// 操作ボタン区分
const EXEC_CANCEL = '0'
const EXEC_CHANGE = '1'

interface UrlParams {
  referenceId: string
}

interface DialogParam {
  isOpen: boolean
  onClose: () => void
  onClick: () => void
  message?: string
}

const DIALOG_PARAM_DEFAULT_VALUE: DialogParam = {
  isOpen: false,
  onClose: () => {},
  onClick: () => {},
}

interface ReservationAcceptability {
  facilityId: string
  facilityName: string
  usageDate: Date
  useFromDatetime: Date
  useToDatetime: Date
  status: string
  name: string
  kana: string
  tel: string
  email: string

  childId: string
  childName: string
  childKana: string

  updateDatetime: string

  closeDatetime: string                   // キャンセル待ち順次通知対象：締切日時
  activeSeq?: number                      // キャンセル待ち順次通知：アクティブ連番
  cancelAutoSendingFlag: string           // 事業マスタ：キャンセル時自動通知機能
  cancelAutoSendingInformation?: string   // 事業マスタ：キャンセル時自動通知説明文
}

export const useAction = () => {
  const errorHandler = useErrorHandle()
  const dispatch = useDispatch()
  const history = useHistory()
  const { addOperationLog } = useOperationLog()
  const [reservationAcceptability, setReservationAcceptability] = useState<ReservationAcceptability>()
  const { referenceId } = useParams<UrlParams>()

  const [putReservationAcceptResultCode, setPutReservationAcceptResultCode] = useState<number>()
  const [dialog, setDialog] = useState<DialogParam>(DIALOG_PARAM_DEFAULT_VALUE)

  useEffect(() => {
    addOperationLog({ operationId: OperationId.OP_00000231 })
 
    const initialize = async () => {
      const [result] = await Promise.all([
        getReservationAcceptability(referenceId),
      ])
      setReservationAcceptability(result)
    }
    dispatch(showLoading(errorHandler(initialize)))

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const goChange = useCallback(() => {
    addOperationLog({ operationId: OperationId.OP_00000232 })

    if (reservationAcceptability == null) {
      // 前画面情報無しの場合はエラーなので何もしない
      alert('reservationAcceptabilityがnull')
      return
    }

    dispatch(
      showLoading({
        process: errorHandler(async () => {
          // 予約更新時
          if (reservationAcceptability.updateDatetime) {
            // 『予約を確定』実行
            let wMessage
            const response = await putReservationAccept(referenceId, EXEC_CHANGE, reservationAcceptability.updateDatetime)
            setPutReservationAcceptResultCode(response.resultCode)
 
            if (response.resultCode) {
              // 登録失敗時
              switch (response.resultCode) {
                case resultErrorCodePutReservationAccept.usageLimitExceeded:
                  // 事業の利用上限超過
                  wMessage = translate('reservationAcceptability.error.reservationAcceptResultCode3')
                  break
                case resultErrorCodePutReservationAccept.consentExpired:
                  // 承諾期限切れ
                  wMessage = translate('reservationAcceptability.error.reservationAcceptResultCode9')
                  break
                // default:
                //   wMessage = translate('reservationAcceptability.error.reservationAcceptResultCodeElse')
                //   break
              }
            } else {
              // 登録成功時
              wMessage = translate('reservationAcceptability.success.changeCompletionMessage')
            }
            setDialog({
              isOpen: true,
              // message: guideTextContent || '',
              message: wMessage,
              onClose: () => {
                handleClose()
              },
              onClick: () => {
                handleClickMember()
              }
            })
          }
        }),
        isHiddenMain: false,
      })
    )

  }, [history, referenceId, reservationAcceptability, addOperationLog])

  const goCancel = useCallback(() => {
      addOperationLog({ operationId: OperationId.OP_00000233 })
  
      if (reservationAcceptability == null) {
        // 前画面情報無しの場合はエラーなので何もしない
        alert('reservationAcceptabilityがnull')
        return
      }

      dispatch(
        showLoading({
          process: errorHandler(async () => {
            // 予約更新時
            if (reservationAcceptability.updateDatetime) {
              // 『キャンセル待ちを取消する』実行
              let wMessage
              const response = await putReservationAccept(referenceId, EXEC_CANCEL, reservationAcceptability.updateDatetime)
              setPutReservationAcceptResultCode(response.resultCode)

              if (response.resultCode) {
                // 登録失敗時
                switch (response.resultCode) {
                  case resultErrorCodePutReservationAccept.usageLimitExceeded:
                    // 事業の利用上限超過
                    wMessage = translate('reservationAcceptability.error.reservationAcceptResultCode3')
                    break
                  case resultErrorCodePutReservationAccept.consentExpired:
                    // 承諾期限切れ
                    wMessage = translate('reservationAcceptability.error.reservationAcceptResultCode9')
                    break
                  // default:
                  //   wMessage = translate('reservationAcceptability.error.reservationAcceptResultCodeElse')
                  //   break
                }
              } else {
                // 登録成功時
                wMessage = translate('reservationAcceptability.success.cancelCompletionMessage')

              }
              setDialog({
                isOpen: true,
                message: wMessage,
                onClose: () => {
                  handleClose()
                },
                onClick: () => {
                  handleClickMember()
                }
              })
            }
          }),
          isHiddenMain: false,
        })
      )
  
    },
    [history, referenceId, reservationAcceptability, addOperationLog]
  )

  const handleClose = useCallback(async () => {
    setDialog(DIALOG_PARAM_DEFAULT_VALUE)
    }, [])
    
  const handleClickMember = useCallback(async() => {
    setDialog(DIALOG_PARAM_DEFAULT_VALUE)
  }, [])

  return {
    reservationAcceptability,
    goChange,
    goCancel,
    putReservationAcceptResultCode,
    dialog,
  }
}

const getReservationAcceptability = async (referenceId: string) => {
  const response = await executeGetReservationAcceptability(referenceId)
  const {
    usageDate,
    useFromDatetime,
    useToDatetime,
    ...other
  } = response.result

  return nullPropsToUndefined({
    ...other,
    usageDate: fromApiYmd(usageDate),
    useFromDatetime: fromApiYmdHms(useFromDatetime),
    useToDatetime: fromApiYmdHms(useToDatetime),
  })
}

const putReservationAccept = async (
  referenceId: string,
  acceptCancel: string,
  updateDatetime: string,
) => {
  return executePutReservationAccept(
    castNonNullable(referenceId),
    undefinedPropsToNull({
      /* 利用終了日時 */
      acceptCancel: acceptCancel,
      /* 更新日時 */
      updateDatetime: updateDatetime,
    })
  )
}

export const getTimeDifference = (baseDate: Date, targetDate: Date) => {
  const diffMilliseconds = differenceInMilliseconds(baseDate, targetDate)

  const diffSeconds = Math.floor(diffMilliseconds / 1000)
  const diffMinutes = Math.floor(diffSeconds / 60)
  const diffHours = Math.floor(diffMinutes / 60)

  const hours = diffHours
  const minutes = diffMinutes % 60
  const seconds = diffSeconds % 60

  return { hours, minutes, seconds }

}
